import React from "react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { Copy, ExternalLink, HelpCircle, Monitor, Smartphone } from "lucide-react";
import { FormCard } from "@/components";
import { Button, Checkbox, Combobox, Input, Label, Tabs, Tooltip } from "@/components/ui";
import { Player } from "@/services/bedrock";
import { PaginatedCombobox } from "@/utils";
import { PreviewDevice } from "./useReviewWidgetPreview";

interface Props {
  players: PaginatedCombobox<Player, false>;
  includeAutoResize: boolean;
  onIncludeAutoResizeChange: (includeAutoResize: boolean) => void;
  previewDevice: PreviewDevice;
  onPreviewDeviceChange: (previewDevice: PreviewDevice) => void;
  embedUrl: string;
  isLoading: boolean;
  previewPlayerName?: string;
  isSamplePreview: boolean;
}

const ReviewWidgetPreviewControls: React.FC<Props> = ({
  players,
  includeAutoResize,
  onIncludeAutoResizeChange,
  previewDevice,
  onPreviewDeviceChange,
  embedUrl,
  isSamplePreview,
}) => {
  const handleCopy = (value: string, message: string) => {
    if (!value) {
      toast.error("Embed URL is not available yet");
      return;
    }
    copy(value);
    toast.success(message);
  };

  return (
    <FormCard>
      <FormCard.Header title="Preview Controls" description="These options only update the preview and snippet." />

      <FormCard.Content>
        <div className="flex flex-col gap-1.5">
          <Label>Preview Device</Label>
          <Tabs value={previewDevice} onValueChange={value => onPreviewDeviceChange(value as PreviewDevice)}>
            <Tabs.List>
              <Tabs.Trigger value="web" className="gap-1.5 px-3">
                <Monitor className="size-3.5" />
                Web
              </Tabs.Trigger>
              <Tabs.Trigger value="mobile" className="gap-1.5 px-3">
                <Smartphone className="size-3.5" />
                Mobile
              </Tabs.Trigger>
            </Tabs.List>
          </Tabs>
        </div>

        <div className="flex items-center gap-1.5">
          <Checkbox
            id="include-auto-resize"
            label="Auto-resize"
            checked={includeAutoResize}
            onCheckedChange={checked => onIncludeAutoResizeChange(!!checked)}
          />
          <Tooltip>
            <Tooltip.Trigger
              render={
                <button
                  type="button"
                  className="inline-flex items-center text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                  aria-label="About auto-resize"
                >
                  <HelpCircle className="size-3.5" />
                </button>
              }
            />
            <Tooltip.Content className="max-w-64">
              When enabled, the snippet listens for height updates from the embed and resizes the iframe to fit. Turn it
              off to keep a fixed height with scrollable content.
            </Tooltip.Content>
          </Tooltip>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Preview Player</Label>
          <Combobox
            {...players}
            showClear
            placeholder="Sample data"
            emptyText="No Players found"
            itemToStringLabel={player => [player.fullName || "No Name", player.email].filter(Boolean).join(" ")}
            itemToLabel={player => (
              <>
                <span className="font-semibold ignore-base-styling">{player.fullName || "No Name"}</span>
                {player.email ? ` - ${player.email}` : ""}
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Embed URL</Label>
          <div className="flex gap-2 min-w-0">
            <Input readOnly value={embedUrl} className="min-w-0 cursor-default bg-slate-100 text-xs text-slate-500" />
            <Tooltip>
              <Tooltip.Trigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCopy(embedUrl, "Embed URL copied")}
                    aria-label="Copy embed URL"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                }
              />
              <Tooltip.Content>Copy embed URL</Tooltip.Content>
            </Tooltip>
            {!isSamplePreview && (
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!embedUrl}
                      onClick={() => window.open(embedUrl, "_blank")}
                      aria-label="Open embed URL"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  }
                />
                <Tooltip.Content>Open embed URL</Tooltip.Content>
              </Tooltip>
            )}
          </div>
        </div>
      </FormCard.Content>
    </FormCard>
  );
};

export { ReviewWidgetPreviewControls };
